import type { ChangeEvent, FormEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, ArrowLeft } from "lucide-react";
import { useApiService } from "../../services/apiService";
import type { Concept, ConceptInput } from "../../types/concept";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

interface ConceptFormProps {
  mode: "create" | "edit";
  conceptId?: number;
  onCancel: () => void;
  onSuccess: (conceptId: number) => void;
}

const emptyConcept: ConceptInput = {
  title: "",
  productType: "",
  targetAudience: "",
  consumptionOccasion: "",
  coreValueProposition: "",
  marketingObjective: "",
  brandFit: "",
  targetMarkets: "",
  positioningStatement: "",
  competitiveSet: "",
  customerInsights: "",
  trendsAddressed: "",
  valueForCustomer: "",
  pricePositioning: "",
  distributionChannel: "",
  packagingConcept: "",
  formatSku: "",
  marketingMix: "",
  launchTiming: "",
  regulatoryRequirements: "",
  successMetrics: "",
  businessCaseAssumptions: "",
  risks: "",
  testStatus: "",
  decisionStage: "",
  recipeHighlights: "",
  shelfLife: "",
  productionFeasibility: "",
};

// Radix SelectItem cannot use an empty string value. Use a sentinel and map it to empty string in state.
const UNSPECIFIED_SENTINEL = "__UNSPECIFIED__" as const;

const decisionStageOptions: { value: string; label: string }[] = [
  { value: UNSPECIFIED_SENTINEL, label: "Unspecified" },
  { value: "Idea exploration", label: "Idea exploration" },
  { value: "Concept development", label: "Concept development" },
  { value: "Validated with customers", label: "Validated with customers" },
  { value: "TG1 ready", label: "TG1 ready" },
  { value: "TG2 ready", label: "TG2 ready" },
  { value: "On hold / Archived", label: "On hold / Archived" },
];

// Use a broad Set<string> here to allow checking against any legacy value coming from the API
const decisionStageOptionValues = new Set<string>(decisionStageOptions.map((option) => option.value as string));

const toInputModel = (concept: Concept): ConceptInput => ({
  title: concept.title ?? "",
  productType: concept.productType ?? "",
  targetAudience: concept.targetAudience ?? "",
  consumptionOccasion: concept.consumptionOccasion ?? "",
  coreValueProposition: concept.coreValueProposition ?? "",
  marketingObjective: concept.marketingObjective ?? "",
  brandFit: concept.brandFit ?? "",
  targetMarkets: concept.targetMarkets ?? "",
  positioningStatement: concept.positioningStatement ?? "",
  competitiveSet: concept.competitiveSet ?? "",
  customerInsights: concept.customerInsights ?? "",
  trendsAddressed: concept.trendsAddressed ?? "",
  valueForCustomer: concept.valueForCustomer ?? "",
  pricePositioning: concept.pricePositioning ?? "",
  distributionChannel: concept.distributionChannel ?? "",
  packagingConcept: concept.packagingConcept ?? "",
  formatSku: concept.formatSku ?? "",
  marketingMix: concept.marketingMix ?? "",
  launchTiming: concept.launchTiming ?? "",
  regulatoryRequirements: concept.regulatoryRequirements ?? "",
  successMetrics: concept.successMetrics ?? "",
  businessCaseAssumptions: concept.businessCaseAssumptions ?? "",
  risks: concept.risks ?? "",
  testStatus: concept.testStatus ?? "",
  decisionStage: concept.decisionStage ?? "",
  recipeHighlights: concept.recipeHighlights ?? "",
  shelfLife: concept.shelfLife ?? "",
  productionFeasibility: concept.productionFeasibility ?? "",
});

export function ConceptForm({ mode, conceptId, onCancel, onSuccess }: ConceptFormProps) {
  const api = useApiService();
  const [formState, setFormState] = useState<ConceptInput>(emptyConcept);
  const [loading, setLoading] = useState(mode === "edit");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = mode === "edit";

  const loadConcept = useCallback(async () => {
    if (!isEdit || !conceptId) return;

    setLoading(true);
    try {
      const data = await api.get<Concept>(`/concepts/${conceptId}`);
      setFormState(toInputModel(data));
      setError(null);
    } catch (err) {
      console.error("Failed to load concept", err);
      setError(err instanceof Error ? err.message : "Unable to load concept metadata");
    } finally {
      setLoading(false);
    }
  }, [api, conceptId, isEdit]);

  useEffect(() => {
    if (isEdit) {
      void loadConcept();
    }
  }, [isEdit, loadConcept]);

  const formTitle = useMemo(() => (isEdit ? "Update concept" : "Create new concept"), [isEdit]);

  const handleChange = (field: keyof ConceptInput) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (
    field: keyof ConceptInput,
    map?: (v: string) => string
  ) => (value: string) => {
    const finalValue = map ? map(value) : value;
    setFormState((prev) => ({ ...prev, [field]: finalValue }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!formState.title.trim()) {
      toast.error("Concept title is required", {
        description: "Please provide a working name before saving.",
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formState,
        title: formState.title.trim(),
      };

      const response = isEdit && conceptId
        ? await api.put<Concept>(`/concepts/${conceptId}`, payload)
        : await api.post<Concept>("/concepts", payload);

      toast.success(isEdit ? "Concept updated" : "Concept created", {
        description: isEdit
          ? "All concept metadata has been refreshed."
          : "The concept is ready for strategic alignment discussions.",
      });

      onSuccess(response.id);
    } catch (err) {
      console.error("Failed to save concept", err);
      toast.error("Unable to save concept", {
        description: err instanceof Error ? err.message : "Please verify the inputs and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Map empty state value to sentinel so the Select shows "Unspecified" instead of the placeholder.
  const decisionStageValueForSelect = formState.decisionStage === "" ? UNSPECIFIED_SENTINEL : formState.decisionStage;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="text-sm">Preparing concept form…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="mx-auto max-w-3xl">
        <CardHeader>
          <CardTitle className="text-xl">We couldn't load this concept</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">{error}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>
              Back to concepts
            </Button>
            <Button onClick={() => void loadConcept()}>Try again</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <ArrowLeft className="h-4 w-4" />
            <button type="button" className="underline" onClick={onCancel}>
              Back to concepts
            </button>
          </div>
          <h1 className="text-2xl font-bold text-foreground">{formTitle}</h1>
          <p className="text-sm text-muted-foreground">
            Capture the essential metadata so teams can compare, prioritize, and launch the right innovations.
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting} className="flex items-center gap-2">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isEdit ? "Save changes" : "Create concept"}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Product & Concept Basics</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <Label htmlFor="title">Concept title</Label>
            <Input id="title" value={formState.title} onChange={handleChange("title")} required placeholder="e.g. Sunrise Wholegrain Breakfast Bake" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="productType">Product type</Label>
            <Input id="productType" value={formState.productType} onChange={handleChange("productType")} placeholder="Frozen pastry, gluten-free bread…" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="targetAudience">Target audience</Label>
            <Textarea id="targetAudience" value={formState.targetAudience} onChange={handleChange("targetAudience")} placeholder="Families seeking convenient weekday breakfasts" rows={3} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="consumptionOccasion">Consumption occasion</Label>
            <Input id="consumptionOccasion" value={formState.consumptionOccasion} onChange={handleChange("consumptionOccasion")} placeholder="On-the-go breakfast, weekend brunch" />
          </div>
          <div className="flex flex-col gap-2 md:col-span-2">
            <Label htmlFor="coreValueProposition">Core value proposition</Label>
            <Textarea id="coreValueProposition" value={formState.coreValueProposition} onChange={handleChange("coreValueProposition")} placeholder="Describe the promise to customers" rows={3} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="marketingObjective">Marketing objective</Label>
            <Input id="marketingObjective" value={formState.marketingObjective} onChange={handleChange("marketingObjective")} placeholder="Drive trial in Q3 across retail partners" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="brandFit">Fit with brand strategy</Label>
            <Textarea id="brandFit" value={formState.brandFit} onChange={handleChange("brandFit")} rows={3} placeholder="Explain how the concept supports the brand portfolio" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Strategic Alignment</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <Label htmlFor="targetMarkets">Target markets</Label>
            <Textarea id="targetMarkets" value={formState.targetMarkets} onChange={handleChange("targetMarkets")} rows={3} placeholder="Nordics retail, DACH foodservice…" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="positioningStatement">Positioning statement</Label>
            <Textarea id="positioningStatement" value={formState.positioningStatement} onChange={handleChange("positioningStatement")} rows={3} placeholder="The convenient healthy snack for busy professionals" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="competitiveSet">Competitive set / benchmarks</Label>
            <Textarea id="competitiveSet" value={formState.competitiveSet} onChange={handleChange("competitiveSet")} rows={3} placeholder="List direct competitors and substitutes" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="valueForCustomer">Value for customer</Label>
            <Textarea id="valueForCustomer" value={formState.valueForCustomer} onChange={handleChange("valueForCustomer")} rows={3} placeholder="Functional, emotional, social benefits" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="pricePositioning">Expected willingness to pay / price positioning</Label>
            <Textarea id="pricePositioning" value={formState.pricePositioning} onChange={handleChange("pricePositioning")} rows={3} placeholder="Premium tier aligned with better-for-you breakfast" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="distributionChannel">Distribution channel</Label>
            <Input id="distributionChannel" value={formState.distributionChannel} onChange={handleChange("distributionChannel")} placeholder="Retail, horeca, QSR, e-commerce" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Customer & Market Insights</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <Label htmlFor="customerInsights">Customer insights</Label>
            <Textarea id="customerInsights" value={formState.customerInsights} onChange={handleChange("customerInsights")} rows={3} placeholder="Needs, pain points, preferences" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="trendsAddressed">Trends addressed</Label>
            <Textarea id="trendsAddressed" value={formState.trendsAddressed} onChange={handleChange("trendsAddressed")} rows={3} placeholder="Health, indulgence, sustainability, clean label…" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Execution Plan</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <Label htmlFor="packagingConcept">Packaging concept</Label>
            <Textarea id="packagingConcept" value={formState.packagingConcept} onChange={handleChange("packagingConcept")} rows={3} placeholder="Materials, sustainability claims, design cues" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="formatSku">Format / SKU</Label>
            <Input id="formatSku" value={formState.formatSku} onChange={handleChange("formatSku")} placeholder="Weight, pack size, case configuration" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="marketingMix">Marketing mix elements</Label>
            <Textarea id="marketingMix" value={formState.marketingMix} onChange={handleChange("marketingMix")} rows={3} placeholder="Planned campaigns, promo types, POS material" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="launchTiming">Launch timing</Label>
            <Input id="launchTiming" value={formState.launchTiming} onChange={handleChange("launchTiming")} placeholder="Seasonality, market entry date" />
          </div>
          <div className="flex flex-col gap-2 md:col-span-2">
            <Label htmlFor="regulatoryRequirements">Regulatory / compliance requirements</Label>
            <Textarea id="regulatoryRequirements" value={formState.regulatoryRequirements} onChange={handleChange("regulatoryRequirements")} rows={3} placeholder="Allergens, nutritional claims, certifications" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Evaluation & Risk</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <Label htmlFor="successMetrics">Success metrics</Label>
            <Textarea id="successMetrics" value={formState.successMetrics} onChange={handleChange("successMetrics")} rows={3} placeholder="Trial, repeat, distribution breadth, margin" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="businessCaseAssumptions">Business case assumptions</Label>
            <Textarea id="businessCaseAssumptions" value={formState.businessCaseAssumptions} onChange={handleChange("businessCaseAssumptions")} rows={3} placeholder="Expected sales, gross margin, ROI timeframe" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="risks">Risks / barriers</Label>
            <Textarea id="risks" value={formState.risks} onChange={handleChange("risks")} rows={3} placeholder="Supply, cost, cannibalization, competitive reaction" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="testStatus">Test status</Label>
            <Input id="testStatus" value={formState.testStatus} onChange={handleChange("testStatus")} placeholder="Concept tested with B2B buyers, pilot runs, etc." />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="decisionStage">Decision stage</Label>
            <Select value={decisionStageValueForSelect} onValueChange={handleSelectChange("decisionStage", (v) => (v === UNSPECIFIED_SENTINEL ? "" : v))}>
              <SelectTrigger id="decisionStage">
                <SelectValue placeholder="Select decision stage" />
              </SelectTrigger>
              <SelectContent>
                {decisionStageOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
                {formState.decisionStage && !decisionStageOptionValues.has(formState.decisionStage) && (
                  <SelectItem value={formState.decisionStage} disabled>
                    {formState.decisionStage} (legacy value)
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Technical Foundations</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <Label htmlFor="recipeHighlights">Recipe / ingredient highlights</Label>
            <Textarea id="recipeHighlights" value={formState.recipeHighlights} onChange={handleChange("recipeHighlights")} rows={3} placeholder="Clean label, local sourcing, nutritional highlights" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="shelfLife">Shelf life / storage conditions</Label>
            <Input id="shelfLife" value={formState.shelfLife} onChange={handleChange("shelfLife")} placeholder="Frozen 12 months, chilled 5 days" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="productionFeasibility">Production feasibility</Label>
            <Textarea id="productionFeasibility" value={formState.productionFeasibility} onChange={handleChange("productionFeasibility")} rows={3} placeholder="Lines used, MOQ, scalability, cost per unit" />
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
